






































































import { extend } from "vee-validate";
import { required, min, confirmed } from "vee-validate/dist/rules";
import { defineComponent, ref, useContext } from "@nuxtjs/composition-api";
import { useUser } from "~/modules/customer/composables/useUser";
import ProfileUpdateForm from "~/modules/customer/pages/MyAccount/MyProfile/ProfileUpdateForm.vue";
import type {
  OnFormComplete,
  OnFormError,
  SubmitEventPayload,
} from "~/modules/customer/types/form";
import type { ProfileUpdateFormFields } from "~/modules/customer/pages/MyAccount/MyProfile/types";
import { useClevertapStore } from "~/stores/clevertap";

extend("required", {
  ...required,
  message: "This field is required",
});

extend("min", {
  ...min,
  message: "The field should have at least {length} characters",
});

extend("confirmed", {
  ...confirmed,
  message: "Passwords don't match",
});

export default defineComponent({
  name: "MyProfile",
  components: {
    ProfileUpdateForm,
  },

  setup() {
    const { loading, updateUser, error, user } = useUser();
    console.log("user: ", user);
    const clevertapStore = useClevertapStore();
    const { app } = useContext();
    const editProfile = ref(false);

    const calculateAge = (dateOfBirth: string): number => {
      if (!dateOfBirth) return 0;

      const birthDate = new Date(dateOfBirth);
      const today = new Date();

      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }

      return age;
    };

    // Function to track profile update with CleverTap
    const trackProfileUpdate = (
      formData: ProfileUpdateFormFields,
      isSuccessful: boolean
    ) => {
      if (!process.client) return;

      try {
        const win = window as any;
        if (!win.clevertap) return;

        // Get current user data (fallback to the form data if user data not available)
        const userData = user.value || formData;

        // Format gender value correctly
        const genderMap = {
          "1": "nam",
          "2": "nữ",
          "3": "khác",
        };

        // Format date correctly (YYYY-MM-DD to DD/MM/YYYY)
        const formatDate = (dateStr: string): string => {
          if (!dateStr) return "";

          try {
            const parts = dateStr.split("-");
            if (parts.length === 3) {
              return `${parts[2]}/${parts[1]}/${parts[0]}`;
            }
          } catch (e) {
            console.error("Error formatting date:", e);
          }

          return dateStr;
        };

        // Prepare tracking data
        const trackingData = {
          is_successful: isSuccessful,
          phone_number: userData.phone_number || "",
          email_address: userData.email || "",
          birthday: formatDate(userData.date_of_birth || ""),
          gender: genderMap[String(userData.gender)] || "khác",
          name: userData.firstname || "",
        };

        // Send event to CleverTap
        clevertapStore.editProfileCleverTap({
          clevertap: win.clevertap,
          data: trackingData,
        });

        // If update was successful, also update the user profile in CleverTap
        if (isSuccessful) {
          // Map gender to M/F/O for CleverTap profile
          const genderProfileMap = {
            "1": "M", // nam -> M
            "2": "F", // nữ -> F
            "3": "O", // khác -> O (Other)
          };

          clevertapStore.userUpdateWebsiteCleverTap({
            clevertap: win.clevertap,
            infor: {
              Identity: userData.phone_number || "",
              Phone: userData.phone_number || "",
              Email: userData.email || "",
              Name: userData.firstname || "",
              DOB: userData.date_of_birth
                ? new Date(userData.date_of_birth)
                : undefined,
              Gender: genderProfileMap[String(userData.gender)] || "O",
            },
          });
        }
      } catch (error) {
        console.error("Error tracking profile update:", error);
      }
    };

    const formHandler = async <T extends () => Promise<unknown>>(
      onSubmit: T,
      onComplete: OnFormComplete,
      onError: OnFormError,
      formData: ProfileUpdateFormFields
    ) => {
      try {
        await onSubmit();
        const actionErr = error.value.changePassword || error.value.updateUser;

        if (actionErr) {
          // Track failed update
          trackProfileUpdate(formData, false);
          onError(actionErr.toString());
        } else {
          // Track successful update
          trackProfileUpdate(formData, true);
          onComplete();
        }
      } catch (err) {
        // Track failed update in case of unexpected error
        trackProfileUpdate(formData, false);
        onError(err.toString());
      }
    };

    const updatePersonalData = ({
      form,
      onComplete,
      onError,
    }: SubmitEventPayload<ProfileUpdateFormFields>) => {
      formHandler(
        async () => updateUser({ user: form }),
        onComplete,
        onError,
        form
      );
      editProfile.value = false;
    };

    return {
      loading,
      updatePersonalData,
      user,
      trackProfileUpdate,
      editProfile,
      calculateAge,
    };
  },
});
